import { desktopApps, type DesktopAppId } from "./apps";
import type { WindowBounds, WindowSnap } from "./windowGeometry";
import type { DesktopWindowContext } from "./windowContext";

export type DesktopWindowState = {
  appId: DesktopAppId;
  instanceId?: string;
  context?: DesktopWindowContext;
  /** Explicit navigation only. Context reports never drive another window's route. */
  navigationSearch?: string;
  /** Desk membership changes visibility only; the mounted application stays alive. */
  deskId?: string;
  minimized: boolean;
  maximized: boolean;
  z: number;
  bounds?: WindowBounds;
  snap?: WindowSnap | null;
};

export const windowId = (item: DesktopWindowState) => item.instanceId ?? item.appId;
export const supportsMultipleWindows = (appId: DesktopAppId) => ["host-files", "files", "workbench"].includes(appId);

type StoredDesktopLayout = {
  version: 1;
  windows: DesktopWindowState[];
};

const validIds = new Set<DesktopAppId>(desktopApps.map((app) => app.id));

export function desktopStorageKey(userId: string) {
  return `base-sul-os:desktop:v1:${userId}`;
}

export function browserDesktopStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readDesktopLayout(storage: Storage | null, userId: string): DesktopWindowState[] | null {
  try {
    if (!storage) return null;
    const raw = storage.getItem(desktopStorageKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StoredDesktopLayout>;
    if (parsed.version !== 1 || !Array.isArray(parsed.windows)) return null;
    const seen = new Set<DesktopAppId>();
    const windows: DesktopWindowState[] = [];
    for (const candidate of parsed.windows) {
      if (!candidate || typeof candidate !== "object") continue;
      const appId = candidate.appId;
      if (typeof appId !== "string" || !validIds.has(appId as DesktopAppId) || seen.has(appId as DesktopAppId)) continue;
      seen.add(appId as DesktopAppId);
      windows.push({
        appId: appId as DesktopAppId,
        minimized: candidate.minimized === true,
        maximized: candidate.maximized === true,
        z: Number.isSafeInteger(candidate.z) && Number(candidate.z) >= 0 ? Math.min(Number(candidate.z), 10_000) : windows.length + 1,
      });
    }
    return windows.slice(0, desktopApps.length);
  } catch {
    return null;
  }
}

export function writeDesktopLayout(storage: Storage | null, userId: string, windows: DesktopWindowState[]) {
  const safe: StoredDesktopLayout = {
    version: 1,
    windows: windows.slice(0, desktopApps.length).map(({ appId, minimized, maximized, z }) => ({
      appId,
      minimized: Boolean(minimized),
      maximized: Boolean(maximized),
      z: Number.isSafeInteger(z) ? Math.max(0, Math.min(z, 10_000)) : 0,
    })),
  };
  try {
    if (!storage) return;
    storage.setItem(desktopStorageKey(userId), JSON.stringify(safe));
  } catch {
    // O desktop continua funcional quando o navegador bloqueia armazenamento local.
  }
}
